import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { useState } from "react";

import { Button } from "@/components/ui/button";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LiquidationsColumns } from "./columns";
import { formattedNumber } from "@/utilities/formatNumbers";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

const DataTableLiquidations = <TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const liquidationsData = useReactTable({
    data: Array.isArray(data) ? data : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 4,
      },
    },
  });

  return (
    <div className="flex flex-col max-h-[550px]">
      <div className="w-full">
        <div className="h-fit min-h-[220px] overflow-y-auto w-full">
          <Table>
            <TableHeader>
              {liquidationsData.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className={
                          header.id === "actions"
                            ? "w-[50px] max-w-[50px] truncate"
                            : undefined
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {liquidationsData.getRowModel().rows?.length ? (
                liquidationsData.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="truncate">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No hay resultados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter className="bg-gray-100 h-[43px]">
              <TableRow>
                <TableCell className="text-left font-medium">Total</TableCell>
                <TableCell className="text-left">
                  {`${formattedNumber(
                    (
                      liquidationsData
                        ?.getPrePaginationRowModel?.()
                        ?.rows?.reduce((acc, row) => {
                          const total = Number(
                            (row.original as LiquidationsColumns)?.total,
                          );
                          return acc + (isNaN(total) ? 0 : total);
                        }, 0) ?? 0
                    ).toFixed(2),
                  )}`}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </div>
        <div className="flex items-center justify-end space-x-2 py-4 w-full">
          <Button
            variant={"outline"}
            className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm"
            size="sm"
            onClick={() => liquidationsData.previousPage()}
            disabled={!liquidationsData.getCanPreviousPage()}
          >
            Anterior
          </Button>
          <Button
            variant={"outline"}
            className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm"
            size="sm"
            onClick={() => liquidationsData.nextPage()}
            disabled={!liquidationsData.getCanNextPage()}
          >
            Siguiente
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataTableLiquidations;
